import { Component, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { AppState } from 'app/app.reducer';
import {
  headerMenuActive,
  loadMyReportsList,
  selectMyReport,
  deleteMyReport,
  resetSearch,
  showSidebar,
  unSetSearch,
} from 'app/redux/actions';
import { ToastNotificationService } from 'app/components';
import { distinctUntilKeyChanged, take, takeUntil } from 'rxjs/operators';
import { MyCoverageService } from 'app/services';
import { Subject } from 'rxjs';

interface ReportItem {
  id: number;
  name: string;
  data: string;
  chartType: string;
  dataType: string;
  shared: boolean;
  featured: boolean;
}

@Component({
  selector: 'app-sidebar-reports-list',
  templateUrl: './sidebar-reports-list.component.html',
  styleUrls: ['./sidebar-reports-list.component.scss'],
})
export class SidebarReportsListComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();
  public readonly pageSize = 20;
  public first = 0;
  public reports: ReportItem[];
  public loading: boolean;
  public loaded: boolean;
  public currentProject: any;
  public reordering: boolean;

  constructor(
    private store: Store<AppState>,
    private notificationService: ToastNotificationService,
    private myCoverageService: MyCoverageService
  ) {
    this.reports = [];
    this.loading = false;
    this.loaded = false;
    this.reordering = false;
  }

  ngOnInit() {
    this.store
      .select('project')
      .pipe(distinctUntilKeyChanged('projectSelected'), takeUntil(this.destroy$))
      .subscribe(({ projectSelected }) => {
        if (projectSelected) {
          this.currentProject = projectSelected;
          this.store.select('myReportsList').pipe(take(1)).subscribe(({ projectId, loaded, loading, errors }) => {
            if (projectId !== projectSelected.value || (!loaded && !loading) || errors) {
              this.store.dispatch(loadMyReportsList({ projectId: projectSelected.value }));
            }
          });
        }
      });

    this.store
      .select('myReportsList')
      .pipe(takeUntil(this.destroy$))
      .subscribe(({ reports, loading, loaded }) => {
        this.loading = loading;
        this.loaded = loaded;
        this.reports = reports.map((r) => this.mapReport(r));
        if (this.first >= this.reports.length) { this.first = 0; }
      });

    this.store
      .select('myReportsList')
      .pipe(distinctUntilKeyChanged('hasAdded'), takeUntil(this.destroy$))
      .subscribe(({ hasAdded }) => {
        if (hasAdded) {
          this.notificationService.showMessage(
            'Report saved successfully.',
            'Success'
          );
        }
      });

    this.store
      .select('myReportsList')
      .pipe(distinctUntilKeyChanged('hasDeleted'), takeUntil(this.destroy$))
      .subscribe(({ hasDeleted }) => {
        if (hasDeleted) {
          this.notificationService.showMessage(
            'Report deleted successfully.',
            'Success'
          );
        }
      });

    this.store
      .select('myReportsList')
      .pipe(distinctUntilKeyChanged('hasUpdated'), takeUntil(this.destroy$))
      .subscribe(({ hasUpdated }) => {
        if (hasUpdated) {
          this.notificationService.showMessage(
            'Report updated successfully.',
            'Success'
          );
        }
      });

    this.store
      .select('myReportsList')
      .pipe(distinctUntilKeyChanged('errors'), takeUntil(this.destroy$))
      .subscribe(({ errors }) => {
        if (errors) {
          this.notificationService.showMessage(
            'Error processing report.',
            'Error'
          );
        }
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onReportClick(report: ReportItem) {
    this.store.dispatch(showSidebar());
    this.store.dispatch(headerMenuActive({ menuActive: 'MyCoverage' }));
    this.store.dispatch(resetSearch({ onReset: true }));
    this.store.dispatch(unSetSearch());
    this.store.dispatch(selectMyReport({ report }));
  }

  formatType(value: string): string {
    return value
      ? value.replace(/[_-]/g, ' ').replace(/\b\w/g, letter => letter.toUpperCase())
      : '—';
  }

  onDeleteClick(report: ReportItem, event: Event) {
    event.stopPropagation();
    this.store.dispatch(
      deleteMyReport({
        projectId: this.currentProject.value,
        reportId: report.id,
      })
    );
  }

  moveReport(report: ReportItem, offset: number, event: Event) {
    event.stopPropagation();
    const index = this.reports.findIndex(item => item.id === report.id);
    const destination = index + offset;
    if (
      this.reordering ||
      destination < 0 ||
      destination >= this.reports.length
    ) {
      return;
    }

    const previous = this.reports;
    const reordered = [...this.reports];
    [reordered[index], reordered[destination]] = [
      reordered[destination],
      reordered[index],
    ];
    this.reports = reordered;
    this.reordering = true;

    this.myCoverageService
      .updateReportOrder(
        this.currentProject.value,
        reordered.map((item) => item.id)
      )
      .subscribe(
        () => {
          this.reordering = false;
          this.store.dispatch(
            loadMyReportsList({ projectId: this.currentProject.value })
          );
        },
        () => {
          this.reports = previous;
          this.reordering = false;
          this.notificationService.showMessage(
            'Unable to save report order.',
            'Error'
          );
        }
      );
  }

  private mapReport(r: any): ReportItem {
    let chartType = '';
    let dataType = '';
    try {
      const parsed = JSON.parse(r.data);
      chartType = (parsed.chartType || '').toLowerCase();
      dataType = (parsed.dataType || '').toLowerCase();
    } catch (_) {}

    return {
      id: r.id,
      name: r.name,
      data: r.data,
      chartType,
      dataType,
      shared: r.shared,
      featured: r.featured,
    };
  }
}
